import { StatusIndicator } from '@heidi/design-system/status-indicator';
import { TypographyCaption } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { Fragment, type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import type {
  ApprovalVerdict,
  ChatItem,
  ToolUseItem,
  TurnFailedItem,
} from '../../../../contracts';
import { ToolUseRow } from '../tool-use-row/tool-use-row';
import { agentTimelineMessages } from './agent-timeline.messages';

export interface AgentTimelineProps {
  items: ChatItem[];
  /**
   * Resolve a pending HITL approval. Returns a Promise that rejects
   * on BE failure so an inline approval card can clear its
   * pending-verdict latch and let the user retry. Resolves on success
   * — the SSE refetch then unmounts the card naturally.
   */
  onResolveApproval?: (
    approvalId: string,
    verdict: ApprovalVerdict,
    reason?: string,
    modifiedInput?: Record<string, unknown> | null
  ) => Promise<void>;
  resolvingApproval?: boolean;
  renderToolUse?: (item: ToolUseItem) => ReactNode;
  renderItem?: (
    item: ChatItem,
    defaultRender: (item: ChatItem) => ReactNode,
    context: AgentTimelineItemContext
  ) => ReactNode;
  /**
   * Vertical rhythm between the items, replacing the default flat 16px gap.
   *
   * This container is the real turn boundary for a chat surface: a user
   * message and the blocks of the answer to it are siblings HERE, not in the
   * wrapper outside. So a host that wants the question separated from its
   * answer has to state it on this element — nothing further out can reach
   * across with a child combinator.
   *
   * Project chat passes the thread tokens from `response-thread`, which are
   * `space-y-*` based. That matters: those tokens express the
   * question-to-answer gap as a `margin-top` override, which REPLACES a
   * `space-y-*` but would STACK ON TOP OF the default `gap-4`. Pass a rhythm
   * or take the default; do not mix them.
   *
   * Left undefined by the agent-harness surfaces, which keep the flat gap.
   */
  rhythmClassName?: string;
}

export interface AgentTimelineItemContext {
  /** The item belongs to a turn that ended with a recovered failure marker. */
  turnFailed: boolean;
}

const DEFAULT_ITEM_CONTEXT: AgentTimelineItemContext = { turnFailed: false };

export const AgentTimeline = ({
  items,
  onResolveApproval,
  resolvingApproval,
  renderToolUse,
  renderItem,
  rhythmClassName,
}: AgentTimelineProps) => {
  const itemContexts = getItemContexts(items);
  const defaultRender = (item: ChatItem) => (
    <TimelineItem
      item={item}
      onResolveApproval={onResolveApproval}
      resolvingApproval={resolvingApproval}
      renderToolUse={renderToolUse}
    />
  );

  return (
    <div className={cn('flex flex-col', rhythmClassName ?? 'gap-4')}>
      {items.map((item, index) => (
        <Fragment key={item.id}>
          {renderItem
            ? renderItem(item, defaultRender, itemContexts[index] ?? DEFAULT_ITEM_CONTEXT)
            : defaultRender(item)}
        </Fragment>
      ))}
    </div>
  );
};

const getItemContexts = (items: ChatItem[]): AgentTimelineItemContext[] => {
  const contexts = items.map((): AgentTimelineItemContext => ({ turnFailed: false }));
  let turnStart = 0;

  const markTurn = (turnEnd: number) => {
    const turnFailed = items
      .slice(turnStart, turnEnd)
      .some((item) => item.kind === 'turn_failed');
    for (let index = turnStart; index < turnEnd; index += 1) {
      contexts[index] = { turnFailed };
    }
  };

  for (let index = 1; index < items.length; index += 1) {
    const item = items[index];
    if (
      item?.kind !== 'user_message' ||
      (!item.text.trim() && item.attachments.length === 0)
    )
      continue;
    markTurn(index);
    turnStart = index;
  }
  markTurn(items.length);

  return contexts;
};

const TimelineItem = ({
  item,
  onResolveApproval,
  resolvingApproval,
  renderToolUse,
}: {
  item: ChatItem;
  onResolveApproval?: AgentTimelineProps['onResolveApproval'];
  resolvingApproval?: boolean;
  renderToolUse?: AgentTimelineProps['renderToolUse'];
}) => {
  switch (item.kind) {
    case 'user_message':
      return (
        <div className="ms-auto max-w-[80%] rounded-xl bg-surface-accent px-4 py-2 text-sm text-text-primary">
          {item.text}
        </div>
      );
    case 'assistant_text':
      return (
        <div
          className={cn(
            'max-w-full rounded-xl bg-surface px-4 py-2 text-sm text-text-primary',
            item.streaming && 'duration-300 animate-in fade-in'
          )}
        >
          {item.text}
        </div>
      );
    case 'thinking':
      return (
        <div className="max-w-full rounded-xl bg-surface-container px-4 py-2 text-sm italic text-text-secondary">
          {item.text}
        </div>
      );
    case 'tool_use':
      return renderToolUse ? (
        renderToolUse(item)
      ) : (
        <ToolUseRow
          item={item}
          onResolveApproval={onResolveApproval}
          resolvingApproval={resolvingApproval}
        />
      );
    case 'turn_cancelled_marker':
      return (
        <div className="mx-auto rounded-full bg-surface-container px-3 py-1 text-xs text-text-secondary">
          <FormattedMessage {...agentTimelineMessages.turnCancelled} />
        </div>
      );
    case 'turn_failed':
      return <TurnFailedStatusCard reason={item.reason} />;
    case 'prompt_context_failed':
      return (
        <div className="rounded-lg border border-validation-destructive-border bg-validation-destructive-muted/40 p-3 text-sm text-validation-destructive">
          {item.message ?? (
            <FormattedMessage {...agentTimelineMessages.promptContextFailed} />
          )}
        </div>
      );
  }
};

export const TurnFailedStatusCard = ({ reason }: Pick<TurnFailedItem, 'reason'>) => (
  <div
    className={cn(
      'inline-flex max-w-full items-center gap-2 self-start rounded-lg border border-border bg-surface py-1.5 pe-3 ps-2.5',
      'duration-300 animate-in fade-in'
    )}
    role="status"
    data-testid="turn-failed-status"
  >
    <StatusIndicator status="alert" size="xs" />
    <TypographyCaption className="min-w-0 break-words text-text-secondary">
      {reason === 'consecutive_tool_failure_limit' ? (
        <FormattedMessage
          defaultMessage="Task stopped after repeated action failures. Send a new message to tell Heidi what to do next."
          id="PpkcexJvf/"
          description="Recovery guidance when repeated agent actions fail"
        />
      ) : (
        <FormattedMessage
          defaultMessage="Heidi couldn’t finish that request. Send another message to try again."
          id="aE1tt1p+ZL"
          description="Generic recovery guidance after an agent turn fails"
        />
      )}
    </TypographyCaption>
  </div>
);
